import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, OnInit, OnDestroy, AfterViewInit, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, NavigationEnd, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TaskCounterService, ActiveTask } from '../../../../shared/task-counter.service';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import { UnifiedTaskCardComponent, TaskCardData } from '../unified-task-card/unified-task-card.component';
import { FilePageLinkService, FilePageLinkSource } from '../../../../shared/file-page-link.service';
import { FilePageScopeComponent } from '../../../../shared/ui/file-page-scope/file-page-scope.component';

/**
 * Precomputed presentation for one row's `Scope` cell (Issue #1616).
 *
 * Built once per data change by {@link TasksPageComponent.applyTaskScopeLinks} so the template
 * never resolves a route on a change-detection pass — the table re-checks on every hover.
 */
export interface TaskScopeView {
  /** Plain-text scope title. The only rendering for rows whose scope cannot be linked. */
  title: string;
  /** Second line of the cell: chapter/section ids, file name, … */
  subtitle: string;
  /** Neutral source consumed by the shared scope component, or `null` for unresolvable scopes. */
  source: FilePageLinkSource | null;
  /** The page title appended after the linked segments, when the page has one. */
  pageTitle: string;
}

/** A task row carrying its precomputed `Scope` presentation (Issue #1616). */
type TaskWithScopeView = ActiveTask & { scopeView?: TaskScopeView };

/** Task dashboard showing all project tasks in a filterable table with expandable detail rows (Issue #1112). */
@Component({
  selector: 'app-tasks-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatListModule,
    MatSnackBarModule,
    RouterModule,
    UnifiedTaskCardComponent,
    FilePageScopeComponent,
  ],
  templateUrl: './tasks-page.component.html',
  styleUrls: ['./tasks-page.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('void', style({ height: '0', opacity: '0', overflow: 'hidden' })),
      state('*', style({ height: '*', opacity: '1' })),
      transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TasksPageComponent implements OnInit, OnDestroy, AfterViewInit {
  private taskCounterService = inject(TaskCounterService);
  private planReviewerService = inject(PlanReviewerService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private filePageLinks = inject(FilePageLinkService);

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns = ['task', 'scope', 'status', 'progress', 'started', 'duration', 'cost'];
  allStatuses: ActiveTask['status'][] = ['pending', 'processing', 'complete', 'failed', 'cancelled'];
  statusFilterControl = new FormControl<string[]>([]);
  statusFilter: string[] = [];
  private projectId = '';

  dataSource = new MatTableDataSource<ActiveTask>([]);
  allTasks: ActiveTask[] = [];
  expandedTaskId: string | null = null;
  isScrolled = false;
  /** Whether older tasks beyond the currently loaded page(s) exist (Issue #1598). */
  hasMoreTasks = false;
  isLoadingMoreTasks = false;

  private cachedTaskCardData = new Map<string, TaskCardData>();
  private chapterIdToPath = new Map<string, string>();
  private chapterLookupPending = new Set<string>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.dataSource.filterPredicate = (task: ActiveTask) => {
      if (this.statusFilter.length === 0) return true;
      return this.statusFilter.includes(task.status);
    };

    this.dataSource.sortingDataAccessor = (task: ActiveTask, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'started':
          return task.startTime?.getTime() ?? 0;
        default:
          return '';
      }
    };

    this.statusFilterControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
      this.statusFilter = values || [];
      this.dataSource.filter = this.statusFilter.length > 0 ? JSON.stringify(this.statusFilter) : '';
    });

    this.taskCounterService.activeTasks$.pipe(takeUntil(this.destroy$)).subscribe(tasks => {
      this.allTasks = tasks;
      this.dataSource.data = tasks;
      this.rebuildCardCache(tasks);
      this.applyTaskScopeLinks(tasks);
      this.resolveNumericChapterIds(tasks);
    });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.projectId = params['planId'] || '';
      // Reset pagination UI state immediately so a previous project's "Load more" affordance
      // doesn't flash while the new project's first page is still loading.
      this.hasMoreTasks = false;
      this.isLoadingMoreTasks = false;
      // Tasks can arrive before the route params settle, and the deep links need the project id,
      // so rebuild them here too (Issue #1616).
      this.applyTaskScopeLinks(this.allTasks);
      if (this.projectId) {
        this.taskCounterService
          .loadExistingTasksForProject(this.projectId)
          .then(() => {
            this.hasMoreTasks = this.taskCounterService.hasMoreTasksForProject(this.projectId);
          })
          .catch(error => {
            ClientLoggingService.log('ERROR', '[TasksPage] Error loading tasks:', this.projectId, error);
            this.snackBar.open('Failed to load tasks. Please refresh the page.', 'Dismiss', { duration: 4000 });
          });
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncExpandedFromUrl());

    this.syncExpandedFromUrl();
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onScroll(): void {
    if (this.scrollContainer) {
      this.isScrolled = this.scrollContainer.nativeElement.scrollTop > 0;
    }
  }

  toggleExpandedTask(task: ActiveTask): void {
    const newId = this.expandedTaskId === task.taskId ? null : task.taskId;
    this.expandedTaskId = newId;

    if (this.projectId) {
      if (newId) {
        this.router.navigate(['/projects', this.projectId, 'tasks', newId], { replaceUrl: true });
      } else {
        this.router.navigate(['/projects', this.projectId, 'tasks'], { replaceUrl: true });
      }
    }
  }

  private syncExpandedFromUrl(): void {
    const url = this.router.url;
    const taskMatch = url.match(/\/tasks\/([^/?#]+)/);
    if (taskMatch && taskMatch[1]) {
      this.expandedTaskId = taskMatch[1];
    } else {
      this.expandedTaskId = null;
    }
  }

  clearStatusFilter(): void {
    this.statusFilterControl.setValue([]);
  }

  /** Fetches the next page of older tasks for this project (Issue #1598). */
  loadMoreTasks(): void {
    if (!this.projectId || this.isLoadingMoreTasks || !this.hasMoreTasks) {
      return;
    }
    this.isLoadingMoreTasks = true;
    this.taskCounterService
      .loadMoreTasksForProject(this.projectId)
      .then(hasMore => {
        this.hasMoreTasks = hasMore;
      })
      .catch(error => {
        ClientLoggingService.log('ERROR', '[TasksPage] Error loading more tasks:', this.projectId, error);
        this.snackBar.open('Failed to load more tasks. Please try again.', 'Dismiss', { duration: 4000 });
      })
      .finally(() => {
        this.isLoadingMoreTasks = false;
      });
  }

  getCachedTaskCardData(task: ActiveTask): TaskCardData {
    const cached = this.cachedTaskCardData.get(task.taskId);
    if (cached) return cached;
    return UnifiedTaskCardComponent.fromActiveTask(task);
  }

  private rebuildCardCache(tasks: ActiveTask[]): void {
    const newCache = new Map<string, TaskCardData>();
    for (const task of tasks) {
      newCache.set(task.taskId, UnifiedTaskCardComponent.fromActiveTask(task));
    }
    this.cachedTaskCardData = newCache;
  }

  /**
   * Placeholder `TaskCounterService` assigns when a task carries no filename
   * (`task-counter.service.ts:367`). It is a sentinel, not a name.
   */
  private static readonly UNKNOWN_FILENAME = 'Unknown File';

  /**
   * Returns a genuine file name, or `null` when all we have is the placeholder.
   *
   * {@link FilePageLinkService.getFileTooltip} resolves in order and takes the first truthy value,
   * so handing it the sentinel short-circuits the chain and produces `File: Unknown File` — on the
   * `aria-label` as well as the tooltip, i.e. the link's accessible name. Billing never hits this
   * because its field is absent rather than filled in when unknown. Returning `null` lets the
   * chain fall through to `File ID: {n}`, which is honest rather than misleading.
   *
   * A string comparison only: resolving the real name would need a metadata lookup, which is
   * exactly the per-data-change cost AC7 rules out.
   */
  private static realFilename(filename: string | undefined | null): string | null {
    return filename && filename !== TasksPageComponent.UNKNOWN_FILENAME ? filename : null;
  }

  /**
   * Precomputes the `Scope` cell presentation onto every row (Issue #1616).
   *
   * Mirrors Billing's `applyTaskDetailLinks()`: the template binds `task.scopeView` rather than
   * calling a resolver, so building a `routerLink` costs one pass per data change instead of one
   * per change-detection cycle. Re-run whenever the task list or the project id changes.
   */
  private applyTaskScopeLinks(tasks: ActiveTask[]): void {
    for (const task of tasks) {
      (task as TaskWithScopeView).scopeView = this.buildTaskScopeView(task);
    }
  }

  /**
   * Builds one row's `Scope` presentation.
   *
   * The cell only becomes a pair of links when the task's metadata resolves to a real file *and* a
   * real page — that is exactly the `files/{id}/pages/{n}` case. Everything else (legacy
   * filename-only rows, tasks with no page number, files still being ingested) keeps the plain
   * text this column has always shown, so no anchor is ever rendered that leads nowhere.
   */
  private buildTaskScopeView(task: ActiveTask): TaskScopeView {
    const scope = this.getTaskScope(task);
    const meta = task.metadata || {};
    const pageTitle = meta['pageTitle'] || meta['page_title'] || '';

    const source: FilePageLinkSource = {
      projectId: task.projectId || this.projectId,
      rawFileId: meta['file_id'] ?? meta['fileId'] ?? null,
      rawPageId: meta['page_number'] ?? meta['pageNumber'] ?? null,
      filename: TasksPageComponent.realFilename(meta['filename'] || task.filename),
      pageTitle,
    };

    const isLinkable =
      !!source.projectId &&
      !!this.filePageLinks.resolveNumericFileId(source) &&
      !!this.filePageLinks.resolvePageNumber(source);

    return {
      title: scope.title,
      subtitle: scope.subtitle,
      source: isLinkable ? source : null,
      pageTitle: isLinkable ? String(pageTitle) : '',
    };
  }

  /**
   * For code-applicability tasks with numeric chapter IDs (legacy API),
   * fetches the book's TOC to resolve IDs into short chapter titles.
   */
  private resolveNumericChapterIds(tasks: ActiveTask[]): void {
    const bookIdsToResolve = new Set<string>();
    for (const task of tasks) {
      if (task.taskType !== 'code-applicability') continue;
      const meta = task.metadata || {};
      const chapters: string[] = meta['specificChapterIds'] || meta['specific_chapter_ids'] || [];
      const bookId = meta['icc_book_id'] || meta['iccBookId'] || '';
      if (!bookId || chapters.length === 0) continue;

      const hasUnresolved = chapters.some(id => /^\d+$/.test(id) && !this.chapterIdToPath.has(id));
      if (hasUnresolved && !this.chapterLookupPending.has(bookId)) {
        bookIdsToResolve.add(bookId);
      }
    }

    for (const bookId of bookIdsToResolve) {
      this.chapterLookupPending.add(bookId);
      this.planReviewerService
        .getIccBookChapters(bookId)
        .then(chapters => {
          for (const ch of chapters) {
            this.chapterIdToPath.set(ch.contentId, this.shortenChapterTitle(ch.title));
          }
          // The Scope subtitle is precomputed (Issue #1616), so resolving chapter titles is a data
          // change like any other — without this the subtitle would keep showing the numeric id
          // that was current when the row was first built.
          this.applyTaskScopeLinks(this.allTasks);
        })
        .catch(err => {
          ClientLoggingService.log('WARN', '[TasksPage] Failed to resolve chapter IDs for book:', bookId, err);
        })
        .finally(() => {
          this.chapterLookupPending.delete(bookId);
        });
    }
  }

  /** Shortens "Chapter 5 General Building Heights and Areas" → "Ch 5 General Building Heights…" */
  private shortenChapterTitle(title: string): string {
    const shortened = title.replace(/^Chapter\s+/i, 'Ch ');
    return shortened.length > 30 ? shortened.substring(0, 28) + '…' : shortened;
  }

  /** Resolves a numeric chapter ID to a short title if available, otherwise returns the raw ID. */
  resolveChapterId(id: string): string {
    return this.chapterIdToPath.get(id) || id;
  }

  retryTask(_taskCardData: TaskCardData): void {
    this.snackBar.open('Task retry is not yet available.', 'Dismiss', { duration: 4000 });
  }

  cancelTask(_taskCardData: TaskCardData): void {
    this.snackBar.open('Task cancellation is not yet available.', 'Dismiss', { duration: 4000 });
  }

  getTaskTypeLabel(taskType?: string): string {
    switch (taskType) {
      case 'code-applicability':
        return 'Applicability Analysis';
      case 'compliance-report':
        return 'Compliance Report';
      case 'plan_ingestion':
      case 'plan-ingestion':
      case 'page-ingestion':
        return 'Page Ingestion';
      case 'cross-reference-graph':
        return 'Cross-Reference Graph';
      default:
        return taskType || 'Task';
    }
  }

  getTaskScope(task: ActiveTask): { title: string; subtitle: string } {
    const meta = task.metadata || {};
    const fileId = meta['file_id'] || meta['fileId'];
    const pageNumber = meta['page_number'] || meta['pageNumber'];
    const pageTitle = meta['pageTitle'] || meta['page_title'] || '';

    let title = '';
    if (fileId && pageNumber) {
      title = `files/${fileId}/pages/${pageNumber}`;
    } else if (pageNumber) {
      title = `pages/${pageNumber}`;
    } else if (task.filename) {
      title = task.filename;
    }
    if (pageTitle) {
      title += title ? ` — ${pageTitle}` : pageTitle;
    }

    let subtitle = '';
    switch (task.taskType) {
      case 'code-applicability': {
        const chapters: string[] = meta['specificChapterIds'] || meta['specific_chapter_ids'] || [];
        const sections: string[] = meta['specificSectionIds'] || meta['specific_section_ids'] || [];
        const maxDepth = meta['maxSectionDepth'] || meta['max_section_depth'];
        const parts: string[] = [];
        if (sections.length > 0) {
          parts.push(sections.join(', '));
        } else if (chapters.length > 0) {
          parts.push(chapters.map(id => this.resolveChapterId(id)).join(', '));
        }
        if (maxDepth) {
          parts.push(`Depth: ${maxDepth}`);
        }
        subtitle = parts.join(' · ');
        break;
      }
      case 'compliance-report': {
        const sectionId = meta['icc_section_id'] || meta['iccSectionId'] || '';
        if (sectionId) {
          subtitle = sectionId;
        }
        break;
      }
      case 'plan_ingestion':
      case 'plan-ingestion':
      case 'page-ingestion': {
        const filename = meta['filename'] || task.filename || '';
        if (filename && !title.includes(filename)) {
          subtitle = filename;
        }
        break;
      }
      default:
        break;
    }

    return { title: title || '—', subtitle };
  }

  formatCost(task: ActiveTask): string {
    const cost = task.costAnalysis?.totalCostUsd;
    if (cost == null || cost === 0) return '—';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  }

  getTaskTypeIcon(taskType?: string): string {
    switch (taskType) {
      case 'code-applicability':
        return 'gavel';
      case 'compliance-report':
        return 'fact_check';
      case 'plan_ingestion':
      case 'plan-ingestion':
      case 'page-ingestion':
        return 'upload_file';
      case 'cross-reference-graph':
        return 'hub';
      default:
        return 'work';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'complete':
        return 'Complete';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  }

  formatRelativeTime(date: Date): string {
    if (!date) return '—';
    const elapsed = Date.now() - date.getTime();
    if (elapsed < 60_000) return '< 1m ago';
    if (elapsed < 3_600_000) return `${Math.floor(elapsed / 60_000)}m ago`;
    if (elapsed < 86_400_000) return `${Math.floor(elapsed / 3_600_000)}h ago`;
    return `${Math.floor(elapsed / 86_400_000)}d ago`;
  }

  formatDuration(task: ActiveTask): string {
    const endTime = task.endTime || new Date();
    const duration = endTime.getTime() - task.startTime.getTime();
    if (duration < 1000) return '< 1s';
    if (duration < 60_000) return `${Math.floor(duration / 1000)}s`;
    const minutes = Math.floor(duration / 60_000);
    const seconds = Math.floor((duration % 60_000) / 1000);
    return `${minutes}m ${seconds}s`;
  }
}
